'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Bot, Sparkles, Smartphone } from 'lucide-react';
import { WhatsAppHeroCards } from '@/components/dashboard/whatsapp/WhatsAppHeroCards';
import {
  WhatsAppMessagesList,
  WhatsAppMessageItem
} from '@/components/dashboard/whatsapp/WhatsAppMessagesList';
import { WhatsAppMessagePreviewModal } from '@/components/dashboard/whatsapp/WhatsAppMessagePreviewModal';
import { WhatsAppQrModal } from '@/components/dashboard/whatsapp/WhatsAppQrModal';
import { WhatsAppChatsInbox } from '@/components/dashboard/whatsapp/WhatsAppChatsInbox';
import { ScheduledNotificationsQueue } from '@/components/dashboard/whatsapp/ScheduledNotificationsQueue';

const DEFAULT_MESSAGES: WhatsAppMessageItem[] = [
  {
    id: 'msg_1',
    title: 'Turno agendado',
    subtitle: 'Al agendar — sin seña, o al pagar la seña',
    trigger: 'Inmediato',
    enabled: true,
    content: '¡Hola {{cliente}}! 🎉 Tu turno para *{{servicio}}* fue confirmado para el *{{fecha}}* a las *{{hora}}* en {{negocio}}. Te esperamos con ganas. Si necesitás cancelar o reprogramar, por favor avisanos con anticipación.'
  },
  {
    id: 'msg_2',
    title: 'Actualización de turno',
    subtitle: 'Reagendado o cancelado · desde dashboard o portal',
    trigger: 'Inmediato al modificar',
    enabled: true,
    content: 'Hola {{cliente}}, te informamos que tu turno para *{{servicio}}* en {{negocio}} fue actualizado. Nueva fecha: *{{fecha}}* a las *{{hora}}*. ¡Cualquier duda escribinos!'
  },
  {
    id: 'msg_3',
    title: 'Recordatorio N días antes',
    subtitle: 'Pide confirmar por texto · default 5 días antes (configurable)',
    trigger: 'Sale 10:00',
    enabled: true,
    content: 'Hola {{cliente}} 👋 Te recordamos que tenés un turno agendado para el *{{fecha}}* a las *{{hora}}* en {{negocio}}. ¿Nos confirmás si vas a poder asistir respondiendo este mensaje? ¡Gracias!'
  },
  {
    id: 'msg_4',
    title: 'Recordatorio día anterior',
    subtitle: '24 hs antes del turno',
    trigger: 'Sale 09:00',
    enabled: true,
    content: '¡Hola {{cliente}}! Mañana *{{fecha}}* te esperamos a las *{{hora}}* para tu turno de *{{servicio}}* en {{negocio}}. Recordá llegar unos minutitos antes. ¡Que tengas lindo día!'
  },
  {
    id: 'msg_5',
    title: 'Recordatorio mismo día',
    subtitle: 'Llega el día del turno',
    trigger: 'Sale 08:00',
    enabled: true,
    content: '¡Hola {{cliente}}! Hoy es tu día de turno en {{negocio}} a las *{{hora}}* ({{servicio}}). ¡Ya tenemos todo preparado para recibirte!'
  }
];

export default function WhatsAppPage() {
  const [activeTab, setActiveTab] = useState<'chats' | 'automatizacion'>('automatizacion');
  const [connected, setConnected] = useState(true);
  const [phone, setPhone] = useState('+54 9 3462 554433');
  const [messages, setMessages] = useState<WhatsAppMessageItem[]>(DEFAULT_MESSAGES);
  const [loading, setLoading] = useState(false);

  // Sincronizar tab con URL search param
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isChats = window.location.search.includes('tab=chats');
      setActiveTab(isChats ? 'chats' : 'automatizacion');
    }
  }, []);

  const handleSelectTab = (tab: 'chats' | 'automatizacion') => {
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      const newUrl = `${window.location.pathname}?tab=${tab}`;
      window.history.replaceState(null, '', newUrl);
    }
  };

  // Modals state
  const [showQrModal, setShowQrModal] = useState(false);
  const [previewingMessage, setPreviewingMessage] = useState<WhatsAppMessageItem | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const [resConfig, resInstance] = await Promise.all([
        fetch('/api/integrations/whatsapp', { signal: controller.signal }),
        fetch('/api/whatsapp/instance', { signal: controller.signal })
      ]);
      clearTimeout(timeoutId);

      const [dataConfig, dataInstance] = await Promise.all([
        resConfig.json(),
        resInstance.json()
      ]);

      if (dataInstance.success && dataInstance.instance) {
        const isConn = dataInstance.instance.connected || dataInstance.instance.status === 'CONNECTED';
        setConnected(isConn);
        if (dataInstance.instance.phone) {
          setPhone(dataInstance.instance.phone);
        }
      } else if (dataConfig.success) {
        setConnected(dataConfig.connected ?? false);
        if (dataConfig.phone) setPhone(dataConfig.phone);
      }

      if (dataConfig.success && dataConfig.messages && dataConfig.messages.length > 0) {
        setMessages(dataConfig.messages);
      }
    } catch (err) {
      console.warn('Syncing whatsapp data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleToggleMessage = (id: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, enabled: !m.enabled } : m))
    );
  };

  const handleConnectPhone = async (newPhone: string) => {
    await fetch('/api/integrations/whatsapp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'toggle_connection',
        connected: true,
        phone: newPhone
      })
    });
    setConnected(true);
    setPhone(newPhone);
    fetchConfig();
  };

  const handleDisconnect = async () => {
    if (!confirm('¿Deseas desvincular este número de WhatsApp?')) return;
    try {
      await Promise.all([
        fetch('/api/whatsapp/instance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'disconnect' })
        }),
        fetch('/api/integrations/whatsapp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'toggle_connection', connected: false })
        })
      ]);
      setConnected(false);
    } catch (err) {
      console.error('Error al desvincular WhatsApp:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Selector de Experiencia: Chats vs Automatización */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-1 border-b border-slate-200/80">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            {activeTab === 'chats' ? (
              <>
                <MessageSquare className="w-5 h-5 text-emerald-600" />
                <span>Chats de WhatsApp</span>
              </>
            ) : (
              <>
                <Bot className="w-5 h-5 text-emerald-600" />
                <span>Automatización para WhatsApp</span>
              </>
            )}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {activeTab === 'chats'
              ? 'Bandeja de conversaciones en vivo con clientes, respuestas rápidas y seguimiento de turnos.'
              : 'Configuración de dispositivo, asistente Aura AI y las 5 plantillas automáticas de AppGenda.'}
          </p>
        </div>

        {/* Pestañas Segmentadas */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-2xl border border-slate-200 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => handleSelectTab('chats')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'chats'
                ? 'bg-white text-emerald-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
            <span>Chats en Vivo</span>
          </button>

          <button
            type="button"
            onClick={() => handleSelectTab('automatizacion')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'automatizacion'
                ? 'bg-white text-emerald-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Bot className="w-3.5 h-3.5 text-emerald-600" />
            <span>Automatización</span>
          </button>
        </div>
      </div>

      {activeTab === 'chats' ? (
        /* VISTA 1: CHATS EN VIVO (Bandeja conversacional interactiva) */
        <WhatsAppChatsInbox />
      ) : (
        /* VISTA 2: AUTOMATIZACIÓN Y PLANTILLAS */
        <>
          {/* Top 2 Boxes: Vinculá tu WhatsApp & Aura Atiende Sola */}
          <WhatsAppHeroCards
            connected={connected}
            phone={phone}
            onOpenQrModal={() => setShowQrModal(true)}
            onDisconnect={handleDisconnect}
          />

          {/* Messages List & Informational Note */}
          <WhatsAppMessagesList
            messages={messages}
            onToggleMessage={handleToggleMessage}
            onPreviewMessage={(msg) => setPreviewingMessage(msg)}
          />

          {/* Cola de Envíos Programados (Motor Asíncrono) */}
          <ScheduledNotificationsQueue />

          {/* QR Modal */}
          <WhatsAppQrModal
            isOpen={showQrModal}
            onClose={() => setShowQrModal(false)}
            onConnected={handleConnectPhone}
          />

          {/* Message Preview Modal */}
          <WhatsAppMessagePreviewModal
            isOpen={Boolean(previewingMessage)}
            onClose={() => setPreviewingMessage(null)}
            message={previewingMessage}
          />
        </>
      )}
    </div>
  );
}
